import type { Vector2, Vector3 } from '@kitware/vtk.js/types';

import type { Maybe } from '@/src/types';
import type { FrameOfReference } from '@/src/utils/frameOfReference';

import { mat3, mat4, vec2, vec3 } from 'gl-matrix';

/**
 * Reference frame from a 2D (x,y[,0]) coord to 3D coord.
 *
 * Represents a transform from an IJ slice (K=0) to an XYZ slice.
 *
 * See the implementation of getReferencePlaneFromSlice for construction.
 */
export interface ReferencePlane {
  /**
   * The transform from 2D to 3D
   */
  t: mat4;
  /**
   * Internal cache of the inverse of t.
   */
  _tInv: Maybe<mat4>;
}

export function createReferencePlane(): ReferencePlane {
  return {
    t: mat4.create(),
    _tInv: null,
  };
}

const tmp3d = vec3.create();
const tmpMat3 = mat3.create();

export function transform2dTo3d(
  pt2d: Vector2,
  refPlane: ReferencePlane,
  out?: Vector3,
): Vector3 {
  const _out = out ?? vec3.create();
  vec3.set(tmp3d, pt2d[0], pt2d[1], 0);
  vec3.transformMat4(_out, tmp3d, refPlane.t);
  return _out as Vector3;
}

function invertMat4(m: mat4): mat4 {
  const minv = mat4.create();
  mat4.invert(minv, m);
  return minv;
}

export function getInverse(refPlane: ReferencePlane): mat4 {
  refPlane._tInv = refPlane._tInv ?? invertMat4(refPlane.t);
  return refPlane._tInv;
}

export function transform3dTo2d(
  pt3d: Vector3,
  refPlane: ReferencePlane,
  out?: Vector2,
): Vector2 {
  const _out = out ?? vec2.create();
  const inv = getInverse(refPlane);
  vec3.transformMat4(tmp3d, pt3d, inv);
  vec2.set(_out, tmp3d[0], tmp3d[1]);
  return _out as Vector2;
}

export function referencePlaneToFrame(
  refPlane: ReferencePlane,
): FrameOfReference {
  // the reference plane is implicitly defined
  // as centered at 0,0,0 with a +Z normal
  const origin: Vector3 = [0, 0, 0];
  vec3.transformMat4(origin, origin, refPlane.t);

  const normal: Vector3 = [0, 0, 1];
  // extract just rotation
  mat3.fromMat4(tmpMat3, refPlane.t);
  vec3.transformMat3(normal, normal, tmpMat3);
  vec3.normalize(normal, normal);

  return {
    planeNormal: normal,
    planeOrigin: origin,
  };
}

const SliceAxisTransforms: Record<0 | 1 | 2, mat4> = {
  0: mat4.fromValues(0, 1, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 0, 0, 1),
  1: mat4.fromValues(1, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0, 1),
  2: mat4.create(),
};

export function getReferencePlaneFromSlice(
  indexToWorld: mat4,
  slice: number,
  sliceAxis: 0 | 1 | 2,
): ReferencePlane {
  const t = mat4.create();
  // z=0 to z=slice
  mat4.fromTranslation(t, [0, 0, slice]);
  // rotate to correct slice axis
  mat4.mul(t, SliceAxisTransforms[sliceAxis], t);
  mat4.mul(t, indexToWorld, t);
  const a = {
    t,
    _tInv: invertMat4(t),
  };

  return a;
}
